import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import useAuth from '../hooks/useAuth';
import { VoiceSessionCoordinator } from '../lib/voice/VoiceSessionCoordinator';
import { ChatContext, VoiceContext } from './chat-contexts';

const API_URL = import.meta.env.VITE_CHAT_API_URL || 'http://localhost:3001';

const FREE_MESSAGE_LIMIT = 5;
const FREE_MESSAGES_KEY = 'ego-free-messages';
const LLM_PROVIDER_KEY = 'ego-llm-provider';

function loadStoredProvider() {
  const stored = localStorage.getItem(LLM_PROVIDER_KEY);
  return stored === 'openai' ? 'openai' : 'anthropic';
}

export default function ChatProvider({ children }) {
  const { session } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [mode, setModeState] = useState('standard');
  const [provider, setProviderState] = useState(loadStoredProvider);
  const [contextUsage, setContextUsage] = useState(null);
  const [starters, setStarters] = useState([]);
  const abortRef = useRef(null);
  const isForkRef = useRef(false);

  // Free message tracking (anonymous users)
  const [freeMessagesUsed, setFreeMessagesUsed] = useState(
    () => parseInt(localStorage.getItem(FREE_MESSAGES_KEY) || '0', 10)
  );
  const messagesRef = useRef(messages);
  const freeMessagesUsedRef = useRef(freeMessagesUsed);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    freeMessagesUsedRef.current = freeMessagesUsed;
  }, [freeMessagesUsed]);

  const isAnonymous = !session;
  const isAtLimit = isAnonymous && freeMessagesUsed >= FREE_MESSAGE_LIMIT;
  const canChat = !!session?.access_token || (isAnonymous && !isAtLimit);

  // Voice state
  const [voiceState, setVoiceState] = useState('idle');
  const [voiceError, setVoiceError] = useState(null);
  const coordinatorRef = useRef(null);
  const voiceAssistantIdRef = useRef(null);
  const autoRecordTimerRef = useRef(null);

  const [isMuted, setIsMuted] = useState(false);

  const isVoiceConnected = voiceState !== 'idle' && voiceState !== 'error';

  const getAuthHeaders = useCallback(() => {
    if (session?.access_token) return { Authorization: `Bearer ${session.access_token}` };
    return {};
  }, [session?.access_token]);

  const toggleChat = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  // Load starters when mode changes
  const loadStarters = useCallback(async (m) => {
    try {
      const res = await fetch(`${API_URL}/api/chat/starters/${m}`);
      if (res.ok) {
        const data = await res.json();
        setStarters(data);
      }
    } catch { /* ignore */ }
  }, []);

  const setProvider = useCallback((p) => {
    const next = p === 'openai' ? 'openai' : 'anthropic';
    setProviderState(next);
    localStorage.setItem(LLM_PROVIDER_KEY, next);
  }, []);

  const setMode = useCallback((m) => {
    setModeState(m);
    loadStarters(m);
    // Sync mode to voice coordinator if connected
    coordinatorRef.current?.setMode(m);
  }, [loadStarters]);

  // Load initial starters
  useEffect(() => {
    loadStarters(mode);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSessions = useCallback(async () => {
    if (!session?.access_token) return;
    try {
      const res = await fetch(`${API_URL}/api/chat/sessions`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setSessions(data);
      }
    } catch { /* ignore */ }
  }, [session?.access_token]);

  const loadSession = useCallback(async (sessionId) => {
    if (!session?.access_token) return;
    setCurrentSessionId(sessionId);
    try {
      const res = await fetch(`${API_URL}/api/chat/history/${sessionId}`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const msgs = (data.messages || data).map(m => ({
          id: m.id,
          role: m.role,
          content: m.content,
          createdAt: m.created_at,
        }));
        setMessages(msgs);
        // Restore mode from session
        if (data.mode) {
          setModeState(data.mode);
          loadStarters(data.mode);
        }
      }
    } catch { /* ignore */ }
  }, [session?.access_token, loadStarters]);

  const startNewSession = useCallback(() => {
    setCurrentSessionId(null);
    setMessages([]);
    setContextUsage(null);
    setModeState('standard');
    loadStarters('standard');
  }, [loadStarters]);

  const sendMessage = useCallback(async (text, { fork = false } = {}) => {
    if (!canChat || !text.trim() || isStreaming) return;

    isForkRef.current = fork;

    // Add user message optimistically (fork starts clean)
    const userMsg = {
      id: crypto.randomUUID(),
      role: 'user',
      content: text,
      createdAt: new Date().toISOString(),
    };
    if (fork) {
      const forkIndicator = {
        id: crypto.randomUUID(),
        role: 'system',
        type: 'fork_indicator',
      };
      setMessages(prev => [...prev, forkIndicator, userMsg]);
    } else {
      setMessages(prev => [...prev, userMsg]);
    }

    // Add empty assistant placeholder
    const assistantId = crypto.randomUUID();
    setMessages(prev => [...prev, {
      id: assistantId,
      role: 'assistant',
      content: '',
      createdAt: new Date().toISOString(),
      streaming: true,
    }]);

    // Increment free message count for anonymous users (before response)
    if (isAnonymous) {
      const newCount = freeMessagesUsedRef.current + 1;
      freeMessagesUsedRef.current = newCount;
      setFreeMessagesUsed(newCount);
      localStorage.setItem(FREE_MESSAGES_KEY, String(newCount));
    }

    setIsStreaming(true);
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      // Build request body
      const body = isAnonymous
        ? {
            message: text,
            history: messagesRef.current
              .filter(m => m.role === 'user' || m.role === 'assistant')
              .map(m => ({ role: m.role, content: m.content })),
            provider,
          }
        : {
            message: text,
            chatSessionId: currentSessionId,
            mode,
            fork,
            // Provider only applies to NEW sessions — resumed sessions are
            // bound to the provider stored server-side.
            ...(currentSessionId ? {} : { provider }),
          };

      const res = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      // Handle 429 rate limit (plain HTTP response, not SSE)
      if (res.status === 429) {
        setMessages(prev => prev.map(m =>
          m.id === assistantId
            ? { ...m, content: 'Too many messages. Try again later or sign up for unlimited access.', streaming: false }
            : m
        ));
        return;
      }

      if (!res.ok) {
        throw new Error(`Server error: ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6);
          if (data === '[DONE]') continue;

          try {
            const event = JSON.parse(data);

            if (event.type === 'session_init' && !isAnonymous) {
              setCurrentSessionId(event.sessionId);
            } else if (event.type === 'text') {
              setMessages(prev => prev.map(m =>
                m.id === assistantId
                  ? { ...m, content: m.content + event.content }
                  : m
              ));
            } else if (event.type === 'tool_call') {
              setMessages(prev => prev.map(m =>
                m.id === assistantId
                  ? { ...m, toolCall: event.name }
                  : m
              ));
            } else if (event.type === 'context_usage' && !isAnonymous) {
              setContextUsage({
                inputTokens: event.inputTokens,
                outputTokens: event.outputTokens,
                totalCostUsd: event.totalCostUsd,
                contextWindow: event.contextWindow,
              });
            } else if (event.type === 'compact' && !isAnonymous) {
              setContextUsage(prev => prev ? { ...prev, compacted: true, preTokens: event.preTokens } : prev);
            } else if (event.type === 'done') {
              setMessages(prev => prev.map(m =>
                m.id === assistantId
                  ? { ...m, streaming: false, toolCall: null }
                  : m
              ));
              if (isForkRef.current) {
                isForkRef.current = false;
                loadSessions();
              }
            } else if (event.type === 'error') {
              setMessages(prev => prev.map(m =>
                m.id === assistantId
                  ? { ...m, content: m.content || `Error: ${event.content}`, streaming: false }
                  : m
              ));
            }
          } catch { /* ignore parse errors */ }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setMessages(prev => prev.map(m =>
          m.id === assistantId
            ? { ...m, content: m.content || 'Failed to connect to EGO server.', streaming: false }
            : m
        ));
      }
    } finally {
      setIsStreaming(false);
      abortRef.current = null;
    }
  }, [canChat, getAuthHeaders, isAnonymous, currentSessionId, isStreaming, mode, provider, loadSessions]);

  const forkSession = useCallback((text) => {
    return sendMessage(text, { fork: true });
  }, [sendMessage]);

  const renameSession = useCallback(async (sessionId, newTitle) => {
    if (!session?.access_token) return;
    try {
      const res = await fetch(`${API_URL}/api/chat/sessions/${sessionId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ title: newTitle }),
      });
      if (res.ok) {
        setSessions(prev => prev.map(s =>
          s.id === sessionId ? { ...s, title: newTitle } : s
        ));
      }
    } catch { /* ignore */ }
  }, [session?.access_token]);

  const deleteSession = useCallback(async (sessionId) => {
    if (!session?.access_token) return;
    try {
      const res = await fetch(`${API_URL}/api/chat/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const deletedIds = new Set(data.deleted || [sessionId]);
        setSessions(prev => prev.filter(s => !deletedIds.has(s.id)));
        if (deletedIds.has(currentSessionId)) {
          startNewSession();
        }
      }
    } catch { /* ignore */ }
  }, [session?.access_token, currentSessionId, startNewSession]);

  // ── Voice methods ─────────────────────────────────────

  const connectVoice = useCallback(async () => {
    if (!session?.access_token || coordinatorRef.current) return;

    setVoiceError(null);
    setIsMuted(false);

    const coordinator = new VoiceSessionCoordinator({
      apiUrl: API_URL,
      getToken: async () => session.access_token,
      onState: (state) => {
        setVoiceState(state);
        // Clear any pending auto-record timer on state change
        if (autoRecordTimerRef.current) {
          clearTimeout(autoRecordTimerRef.current);
          autoRecordTimerRef.current = null;
        }
        // Auto-start recording when entering wait state (continuous conversation loop)
        // Skip if muted — read from coordinator ref to avoid stale closure
        if (state === 'wait' && !coordinatorRef.current?.isMuted) {
          autoRecordTimerRef.current = setTimeout(() => {
            autoRecordTimerRef.current = null;
            coordinatorRef.current?.startRecording();
          }, 500);
        }
      },
      onSessionInit: (sessionId) => {
        setCurrentSessionId(sessionId);
      },
      onMessage: (msg) => {
        if (msg.type === 'resp_start') {
          // User's transcribed message
          const userMsg = {
            id: crypto.randomUUID(),
            role: 'user',
            content: msg.userText,
            createdAt: new Date().toISOString(),
            source: 'voice',
          };
          setMessages(prev => [...prev, userMsg]);

          // Create assistant placeholder
          const assistantId = crypto.randomUUID();
          voiceAssistantIdRef.current = assistantId;
          setMessages(prev => [...prev, {
            id: assistantId,
            role: 'assistant',
            content: '',
            createdAt: new Date().toISOString(),
            streaming: true,
            source: 'voice',
          }]);
        } else if (msg.type === 'output_text') {
          const aId = voiceAssistantIdRef.current;
          if (aId) {
            setMessages(prev => prev.map(m =>
              m.id === aId ? { ...m, content: m.content + msg.delta } : m
            ));
          }
        } else if (msg.type === 'resp_stop') {
          const aId = voiceAssistantIdRef.current;
          if (aId) {
            setMessages(prev => prev.map(m =>
              m.id === aId ? { ...m, streaming: false } : m
            ));
          }
          voiceAssistantIdRef.current = null;
        }
      },
      onMetrics: () => {
        // Could display latency metrics in future
      },
      onError: (code, message) => {
        setVoiceError(`${code}: ${message}`);
      },
    });

    coordinatorRef.current = coordinator;
    await coordinator.connect(currentSessionId, mode, provider);
  }, [session?.access_token, currentSessionId, mode, provider]);

  const disconnectVoice = useCallback(() => {
    if (autoRecordTimerRef.current) {
      clearTimeout(autoRecordTimerRef.current);
      autoRecordTimerRef.current = null;
    }
    coordinatorRef.current?.destroy();
    coordinatorRef.current = null;
    setVoiceState('idle');
    setVoiceError(null);
    setIsMuted(false);
  }, []);

  const startRecording = useCallback(() => {
    coordinatorRef.current?.startRecording();
  }, []);

  const stopRecording = useCallback(() => {
    coordinatorRef.current?.stopRecording();
  }, []);

  const stopPlayback = useCallback(() => {
    coordinatorRef.current?.stopPlayback();
  }, []);

  const toggleMute = useCallback(() => {
    const coordinator = coordinatorRef.current;
    if (!coordinator) return;

    if (coordinator.isMuted) {
      coordinator.unmute();
      setIsMuted(false);
      // If in wait state, restart auto-record after unmuting
      if (coordinator.voiceState === 'wait') {
        autoRecordTimerRef.current = setTimeout(() => {
          autoRecordTimerRef.current = null;
          coordinatorRef.current?.startRecording();
        }, 500);
      }
    } else {
      // Clear any pending auto-record before muting
      if (autoRecordTimerRef.current) {
        clearTimeout(autoRecordTimerRef.current);
        autoRecordTimerRef.current = null;
      }
      coordinator.mute();
      setIsMuted(true);
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (autoRecordTimerRef.current) clearTimeout(autoRecordTimerRef.current);
      coordinatorRef.current?.destroy();
    };
  }, []);

  const contextValue = useMemo(() => ({
      isOpen,
      sessions,
      currentSessionId,
      messages,
      isStreaming,
      mode,
      provider,
      contextUsage,
      starters,
      freeMessagesUsed,
      isAtLimit,
      isAnonymous,
      toggleChat,
      sendMessage,
      setMode,
      setProvider,
      forkSession,
      renameSession,
      deleteSession,
      loadSessions,
      loadSession,
      startNewSession,
      getAuthHeaders,
      FREE_MESSAGE_LIMIT,
    }), [
      isOpen,
      sessions,
      currentSessionId,
      messages,
      isStreaming,
      mode,
      provider,
      contextUsage,
      starters,
      freeMessagesUsed,
      isAtLimit,
      isAnonymous,
      toggleChat,
      sendMessage,
      setMode,
      setProvider,
      forkSession,
      renameSession,
      deleteSession,
      loadSessions,
      loadSession,
      startNewSession,
      getAuthHeaders,
    ]);

  const voiceValue = useMemo(() => ({
      voiceState,
      voiceError,
      isVoiceConnected,
      isMuted,
      connectVoice,
      disconnectVoice,
      startRecording,
      stopRecording,
      stopPlayback,
      toggleMute,
    }), [
      voiceState,
      voiceError,
      isVoiceConnected,
      isMuted,
      connectVoice,
      disconnectVoice,
      startRecording,
      stopRecording,
      stopPlayback,
      toggleMute,
    ]);

  return (
    <ChatContext.Provider value={contextValue}>
      <VoiceContext.Provider value={voiceValue}>
        {children}
      </VoiceContext.Provider>
    </ChatContext.Provider>
  );
}
